import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-black/10" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="container-lf flex items-center justify-between h-24">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="/logo.png"
            alt="L F Building & Maintenance Ltd"
            className="h-20 md:h-24 w-auto transition-transform group-hover:scale-105"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-bold text-lg tracking-wide uppercase">L F Building</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">& Maintenance Ltd</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  isActive ? "text-brand" : "text-brand-ink hover:text-brand"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && <span className="absolute left-4 right-4 -bottom-[1px] h-[2px] bg-brand" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm font-semibold text-brand-ink hover:text-brand transition-colors"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
          <Link to="/contact" data-testid="nav-cta">
            <Button className="rounded-none bg-brand hover:bg-brand-hover text-white font-semibold uppercase tracking-wider px-6 h-11">
              Get a Quote
            </Button>
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-11 h-11 grid place-items-center border border-black/20 hover:bg-brand hover:text-white hover:border-brand transition-colors"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/10 bg-white" data-testid="mobile-menu">
          <div className="container-lf py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-semibold uppercase tracking-wider border-l-4 transition-all ${
                    isActive ? "border-brand text-brand bg-brand/5" : "border-transparent text-brand-ink hover:border-brand hover:text-brand"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-4 flex flex-col gap-3 pt-4 border-t border-black/10">
              <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-sm font-semibold px-4">
                <Phone className="w-4 h-4 text-brand" /> {COMPANY.phone}
              </a>
              <Link to="/contact" onClick={() => setOpen(false)}>
                <Button className="w-full rounded-none bg-brand hover:bg-brand-hover text-white font-semibold uppercase tracking-wider h-12">
                  Get a Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
